/**
 * LiveMeetingPage - full-page live meeting experience.
 *
 * Route: /meeting/live
 *
 * Opened in a new tab from the persistent live meeting toast. The page
 * establishes its own independent WebSocket connection (via useLiveMeetingSession)
 * so it receives the live stream even if the originating tab is navigated away.
 *
 * Layout: AppShell (left nav + main + right rail)
 *   - Left nav: AppNav (standard module nav, collapsed on entry)
 *   - Main: content header + 2-column resizable body (Transcript | Suggested Questions)
 *   - Right rail: Tracked Questions panel (open on entry)
 *
 * Design rules: no Tailwind, no inline styles, CSS vars only.
 */

import {
  AutocompleteField,
  Button,
  CodeIcon,
  IconSize,
  Select,
  SelectItem,
  SelectSeparator,
  TargetIcon,
  Tooltip,
} from '@protoss/ui';
import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { ResizableDivider } from '../components/ResizableDivider';
import { AppShell, type RightRailItem } from '../components/shell';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { LiveTranscriptPanel } from '../features/live-meeting/components/LiveTranscriptPanel';
import { SuggestedQuestionsPanel } from '../features/live-meeting/components/SuggestedQuestionsPanel';
import { TrackedQuestionsPanel } from '../features/live-meeting/components/TrackedQuestionsPanel';
import { useDealExtract } from '../features/live-meeting/hooks/useDealExtract';
import { useLiveMeetingSession } from '../features/live-meeting/hooks/useLiveMeetingSession';
import { useSuggestedQuestions } from '../features/live-meeting/hooks/useSuggestedQuestions';
import type {
  SalesDealAnswerInput,
  SalesDealSummary,
  WorkspaceProductSummary,
} from '../features/sales/services/salesApi';
import { salesApi } from '../features/sales/services/salesApi';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import './live-meeting-page.css';

const DEFAULT_LEFT_WIDTH = undefined;
const MIN_COLUMN_WIDTH = 240;
const MAX_COLUMN_WIDTH = 900;

const STATUS_LABELS: Record<string, string> = {
  disconnected: 'Disconnected',
  connecting: 'Connecting…',
  waiting: 'Waiting for meeting',
  active: 'Live',
  ended: 'Ended',
  error: 'Error',
};

export function LiveMeetingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { urlToken } = useParams<{ urlToken?: string }>();

  const { workspace, modules } = useWorkspace();

  // Derive moduleKey from the URL token segment (e.g. 'so' → 'sales').
  const moduleFromUrl = urlToken
    ? (modules.find((m) => m.urlToken === urlToken)?.key ?? null)
    : null;
  const resolvedModuleKey = moduleFromUrl;

  // Session pinning: if the URL has ?session=<uuid> at page load, the hook connects to that
  // specific session (crash recovery / direct link). Captured once via useState so that the
  // URL update that writes ?session= when a new meeting starts does NOT cause the hook to
  // switch into pinned mode mid-session - which would remove the WS from workspaceSubscribers
  // and make the client miss lm:session_connected broadcasts for subsequent meetings.
  const [pinnedSessionUuid] = React.useState<string | null>(() => searchParams.get('session'));

  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  // Apply layout config synchronously before first paint and clean up on unmount.
  useLayoutConfig({
    collapseLeftNav: true,
    showRightRail: true,
    showRightUtilityPane: { mode: 'tracked-questions' },
  });

  const session = useLiveMeetingSession(workspace?.uuid, resolvedModuleKey, pinnedSessionUuid);

  // Keep ?session= in the URL in sync with the active session UUID so the page is
  // bookmarkable and crash-recoverable. Uses replace so this doesn't pollute history.
  React.useEffect(() => {
    if (!session.sessionUuid) return;
    if (searchParams.get('session') !== session.sessionUuid) {
      setSearchParams({ session: session.sessionUuid }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.sessionUuid]);

  // Deal link bar state
  const [availableDeals, setAvailableDeals] = React.useState<SalesDealSummary[]>([]);
  const [workspaceProducts, setWorkspaceProducts] = React.useState<WorkspaceProductSummary[]>([]);
  const [isLinkingDeal, setIsLinkingDeal] = React.useState(false);
  const [linkDealError, setLinkDealError] = React.useState<string | null>(null);
  // When true, show the deal picker even if a deal is already linked (for re-linking)
  const [showDealPicker, setShowDealPicker] = React.useState(false);
  // Inline create-deal form state
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const [createDealCompany, setCreateDealCompany] = React.useState('');
  const [createDealProductId, setCreateDealProductId] = React.useState<number | null>(null);
  const [companySuggestions, setCompanySuggestions] = React.useState<string[]>([]);
  const [isCreatingDeal, setIsCreatingDeal] = React.useState(false);
  // Tracks which deal is selected in the picker dropdown (optimistic, before WS confirms)
  const [localSelectedDealUuid, setLocalSelectedDealUuid] = React.useState<string | null>(null);

  // Load available deals, workspace products, and company suggestions when a session starts
  React.useEffect(() => {
    if (session.sessionUuid === null) return;
    Promise.all([
      salesApi.listDeals(),
      salesApi.getWorkspaceProducts(),
      salesApi.getCompanySuggestions(),
    ])
      .then(([{ deals }, { products }, { companies }]) => {
        setAvailableDeals(deals);
        setWorkspaceProducts(products);
        setCompanySuggestions(companies);
        // Auto-select the only product so the create form works without a dropdown
        if (products.length === 1 && products[0]) {
          setCreateDealProductId(products[0].id);
        }
      })
      .catch((err: unknown) => {
        console.error('[LiveMeetingPage] failed to load deals/products', err);
      });
  }, [session.sessionUuid]);

  const handleLinkDeal = React.useCallback(
    (dealUuid: string) => {
      if (!session.sessionUuid || isLinkingDeal) return;
      setIsLinkingDeal(true);
      setLinkDealError(null);
      salesApi
        .linkLiveMeetingDeal(session.sessionUuid, { dealUuid })
        .then(() => {
          setShowDealPicker(false);
          setShowCreateForm(false);
        })
        .catch((err: unknown) => {
          console.error('[LiveMeetingPage] failed to link deal', err);
          setLinkDealError('Failed to link deal. Please try again.');
        })
        .finally(() => setIsLinkingDeal(false));
    },
    [session.sessionUuid, isLinkingDeal]
  );

  const handleCreateDeal = React.useCallback(() => {
    const company = createDealCompany.trim();
    if (!company || !session.sessionUuid || isCreatingDeal || isLinkingDeal) return;
    const sessionUuid = session.sessionUuid;
    setIsCreatingDeal(true);
    setLinkDealError(null);
    salesApi
      .startDeal(company, createDealProductId)
      .then(({ deal }) => {
        setAvailableDeals((prev) => [...prev, deal]);
        setCreateDealCompany('');
        setShowCreateForm(false);
        setLocalSelectedDealUuid(deal.uuid);
        return salesApi.linkLiveMeetingDeal(sessionUuid, { dealUuid: deal.uuid });
      })
      .catch((err: unknown) => {
        console.error('[LiveMeetingPage] failed to create deal', err);
        setLinkDealError('Failed to create deal. Please try again.');
      })
      .finally(() => setIsCreatingDeal(false));
  }, [createDealCompany, createDealProductId, session.sessionUuid, isCreatingDeal, isLinkingDeal]);

  // Background AI extraction - runs independently of the transcript rendering path.
  const {
    sections,
    totalCount,
    answeredCount,
    answeredValues,
    isLoading: isLoadingExtract,
    isInferring: isExtractInferring,
    reset: resetExtract,
  } = useDealExtract({
    transcriptChunks: session.transcriptChunks,
    sessionStatus: session.status,
    dealUuid: session.dealUuid,
  });

  // Save live-captured answers back to the deal when the session ends.
  const savedRef = React.useRef(false);
  const [saveFeedback, setSaveFeedback] = React.useState<string | null>(null);
  const [saveDone, setSaveDone] = React.useState(false);

  // Reset save state when a new session starts so the second meeting's answers are saved.
  React.useEffect(() => {
    if (session.status === 'active') {
      savedRef.current = false;
      setSaveFeedback(null);
      setSaveDone(false);
    }
  }, [session.status]);

  React.useEffect(() => {
    if (session.status !== 'ended') return;
    if (isExtractInferring) return; // wait for any in-flight AI inference to finish
    if (!session.dealUuid) return;
    if (savedRef.current) return;
    const entries = Object.entries(answeredValues);
    savedRef.current = true; // guard before async call - prevent double-save if deps re-trigger
    if (entries.length === 0) {
      setSaveDone(true);
      return;
    }
    const payload: SalesDealAnswerInput[] = entries.map(([fieldKey, value]) => ({
      fieldKey,
      value,
    }));
    salesApi
      .saveDealQuestionnaire(session.dealUuid, payload)
      .then(() =>
        setSaveFeedback(`${entries.length} answer${entries.length === 1 ? '' : 's'} saved to deal`)
      )
      .catch(() => setSaveFeedback('Failed to save answers to deal'))
      .finally(() => setSaveDone(true));
  }, [session.status, session.dealUuid, isExtractInferring, answeredValues]);

  // AI suggested questions - separate concern from tracked questions.
  const {
    activeSuggestions,
    askedSuggestions,
    markAsked,
    dismissSuggestion,
    isInferring: isSuggestInferring,
    reset: resetSuggestions,
  } = useSuggestedQuestions({
    transcriptChunks: session.transcriptChunks,
    sessionStatus: session.status,
    sections,
    sessionUuid: session.sessionUuid,
    isLoadingExtract,
  });

  // Dev-only: reset suggested + tracked questions to initial state without clearing transcript.
  const handleDemoReset = React.useCallback(() => {
    resetSuggestions();
    resetExtract();
  }, [resetSuggestions, resetExtract]);

  // Transcript column resize state.
  const [leftWidth, setLeftWidth] = React.useState<number | undefined>(DEFAULT_LEFT_WIDTH);
  const leftPanelRef = React.useRef<HTMLElement>(null);

  const handleLeftResize = React.useCallback((delta: number) => {
    setLeftWidth((prev) => {
      const base = prev ?? leftPanelRef.current?.offsetWidth ?? MIN_COLUMN_WIDTH;
      return Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, base + delta));
    });
  }, []);

  const leftNav = (
    <AppNav
      selected={null}
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const rightRailItems: RightRailItem[] = [
    {
      id: 'tracked-questions',
      label: 'Tracked Questions',
      icon: <TargetIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />,
      content: (
        <TrackedQuestionsPanel
          sections={sections}
          totalCount={totalCount}
          answeredCount={answeredCount}
          isLoading={isLoadingExtract}
          isInferring={isExtractInferring}
          noDeal={!session.dealUuid}
          saveFeedback={saveFeedback ?? undefined}
        />
      ),
      noPadding: true,
    },
    // DEV-only: raw RTMS event inspector + webhook system event log
    ...(import.meta.env.DEV
      ? [
          {
            id: 'meeting-events',
            label: 'Meeting Events',
            icon: <CodeIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />,
            content: (
              <MeetingEventDebugRail
                rawEventsByType={session.rawEventsByType}
                systemEventLog={session.systemEventLog}
                rawTranscriptLog={session.rawTranscriptLog}
              />
            ),
            noPadding: true,
          } satisfies RightRailItem,
        ]
      : []),
  ];

  const main = (
    <div className="lm-page__main">
      {/* ── Content header ────────────────────────────────────── */}
      <div className="lm-page__content-header">
        <div className="lm-page__content-header-start">
          <h1 className="lm-page__title">Live Meeting</h1>
          <Tooltip.Simple label={STATUS_LABELS[session.status] ?? session.status}>
            <span
              className={`lm-page__status-dot lm-page__status-dot--${session.status}`}
              aria-live="polite"
              aria-label={STATUS_LABELS[session.status] ?? session.status}
            />
          </Tooltip.Simple>
          {session.sessionUuid !== null && (
            <div className="lm-page__deal-link-inline">
              {session.dealUuid && !showDealPicker ? (
                // ── Linked deal: name + Change / spinner / Open button ──
                <>
                  <span className="lm-page__deal-link-name">{session.dealTitle}</span>
                  {session.status === 'ended' ? (
                    saveDone ? (
                      // Processing complete - open the deal page
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => navigate(`/so/deal/${session.dealUuid}`)}
                        aria-label="Open deal page"
                      >
                        Open
                      </Button>
                    ) : (
                      // Backend processing in progress - muted spinner button
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled
                        aria-label="Saving answers…"
                      >
                        <span className="lm-page__deal-link-spinner" aria-hidden="true" />
                        Saving…
                      </Button>
                    )
                  ) : (
                    <>
                      <button
                        type="button"
                        className="lm-page__deal-link-change"
                        onClick={() => {
                          setShowDealPicker(true);
                          setLocalSelectedDealUuid(null);
                          setShowCreateForm(false);
                          setCreateDealCompany('');
                        }}
                        disabled={isLinkingDeal}
                        aria-label="Change linked deal"
                      >
                        Change
                      </button>
                      {isLinkingDeal && (
                        <span className="lm-page__deal-link-spinner" aria-hidden="true" />
                      )}
                    </>
                  )}
                </>
              ) : (
                // ── Deal picker + optional inline create form to the right ─
                <>
                  <div className="lm-page__deal-link-select">
                    <Select
                      value={localSelectedDealUuid ?? undefined}
                      onValueChange={(v) => {
                        if (v === '__create__') {
                          setShowCreateForm(true);
                        } else {
                          setLocalSelectedDealUuid(v);
                          handleLinkDeal(v);
                        }
                      }}
                      placeholder={showDealPicker ? 'Change deal…' : 'Link a deal…'}
                      disabled={isLinkingDeal || showCreateForm}
                      size="sm"
                    >
                      {availableDeals.map((deal) => (
                        <SelectItem key={deal.uuid} value={deal.uuid}>
                          {deal.title}
                        </SelectItem>
                      ))}
                      {availableDeals.length > 0 && <SelectSeparator />}
                      <SelectItem value="__create__">New deal…</SelectItem>
                    </Select>
                  </div>
                  {showCreateForm && (
                    <form
                      className="lm-page__deal-create-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleCreateDeal();
                      }}
                    >
                      <AutocompleteField
                        className="lm-page__deal-create-autocomplete"
                        value={createDealCompany}
                        onChange={setCreateDealCompany}
                        options={companySuggestions}
                        placeholder="Company name"
                        disabled={isCreatingDeal}
                        size="compact"
                      />
                      <div className="lm-page__deal-create-product">
                        <Select
                          value={
                            createDealProductId !== null ? String(createDealProductId) : undefined
                          }
                          onValueChange={(v) => setCreateDealProductId(Number(v))}
                          placeholder="Product"
                          size="sm"
                        >
                          {workspaceProducts.map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>
                              {p.productName}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                      <button
                        type="submit"
                        className="lm-page__deal-create-submit"
                        disabled={
                          !createDealCompany.trim() ||
                          isCreatingDeal ||
                          (workspaceProducts.length > 0 && createDealProductId === null)
                        }
                      >
                        {isCreatingDeal ? (
                          <span className="lm-page__deal-link-spinner" aria-hidden="true" />
                        ) : (
                          'Start deal'
                        )}
                      </button>
                      <button
                        type="button"
                        className="lm-page__deal-link-change"
                        onClick={() => {
                          setShowCreateForm(false);
                          setCreateDealCompany('');
                        }}
                        disabled={isCreatingDeal}
                      >
                        Cancel
                      </button>
                      {linkDealError && (
                        <span className="lm-page__deal-link-error" role="alert">
                          {linkDealError}
                        </span>
                      )}
                    </form>
                  )}
                  {showDealPicker && session.dealUuid && !showCreateForm && (
                    <button
                      type="button"
                      className="lm-page__deal-link-change"
                      onClick={() => setShowDealPicker(false)}
                      disabled={isLinkingDeal}
                    >
                      Cancel
                    </button>
                  )}
                  {isLinkingDeal && (
                    <span className="lm-page__deal-link-spinner" aria-hidden="true" />
                  )}
                  {!showCreateForm && linkDealError && (
                    <span className="lm-page__deal-link-error" role="alert">
                      {linkDealError}
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {session.status === 'active' && (
          <div className="lm-page__autosave" aria-live="polite" aria-label="Auto-saving">
            <span className="lm-page__autosave-label">Auto-saving</span>
            <span className="lm-inferring" aria-hidden="true">
              <span className="lm-inferring__dot" />
              <span className="lm-inferring__dot" />
              <span className="lm-inferring__dot" />
            </span>
          </div>
        )}
        {import.meta.env.DEV && (
          <Tooltip.Simple label="Reset suggested questions and tracked question answers (dev only)">
            <button type="button" className="dev-logs-btn" onClick={handleDemoReset}>
              reset
            </button>
          </Tooltip.Simple>
        )}
      </div>

      {/* ── 2-column resizable body ───────────────────────────── */}
      <div className="lm-page__body">
        {/* Left: Transcript */}
        <section
          ref={leftPanelRef}
          className="lm-page__panel"
          style={
            leftWidth !== undefined ? ({ width: leftWidth } as React.CSSProperties) : undefined
          }
          aria-label="Live transcript"
        >
          <div className="lm-page__panel-header">
            <h2 className="lm-page__panel-title">Transcript</h2>
          </div>
          <LiveTranscriptPanel
            segments={session.segments}
            status={session.status}
            hasMoreTranscript={session.hasMoreTranscript}
            isLoadingOlderTranscript={session.isLoadingOlderTranscript}
            onLoadOlderTranscript={session.loadOlderTranscript}
          />
        </section>

        <ResizableDivider
          direction="vertical"
          onResize={handleLeftResize}
          ariaLabel="Resize transcript panel"
        />

        {/* Right: AI Suggested Questions */}
        <section
          className="lm-page__panel lm-page__panel--flex"
          aria-label="AI suggested questions"
        >
          <div className="lm-page__panel-header">
            <h2 className="lm-page__panel-title">Suggested Questions</h2>
            {isSuggestInferring && (
              <div className="lm-inferring" aria-live="polite" aria-label="AI is updating">
                <span className="lm-inferring__dot" aria-hidden="true" />
                <span className="lm-inferring__dot" aria-hidden="true" />
                <span className="lm-inferring__dot" aria-hidden="true" />
              </div>
            )}
          </div>
          <SuggestedQuestionsPanel
            activeSuggestions={activeSuggestions}
            askedSuggestions={askedSuggestions}
            onAsk={markAsked}
            onDismiss={dismissSuggestion}
            sections={sections}
            isInferring={isSuggestInferring}
            noDeal={!session.dealUuid}
          />
        </section>
      </div>
    </div>
  );

  return <AppShell leftNav={leftNav} main={main} rightRailItems={rightRailItems} />;
}

// ── MeetingEventDebugRail ─────────────────────────────────────────────────────
// DEV-only right-rail panel: webhook-driven participant events (top) and latest
// raw WS payload per event type (bottom). Helps debug end-to-end event delivery.

import type { SystemEventLogEntry } from '../features/live-meeting/types/live-meeting';

function MeetingEventDebugRail({
  rawEventsByType,
  systemEventLog,
  rawTranscriptLog,
}: {
  rawEventsByType: Record<string, unknown>;
  systemEventLog: SystemEventLogEntry[];
  rawTranscriptLog: Record<string, unknown>[];
}) {
  const wsEntries = Object.entries(rawEventsByType);
  return (
    <div className="lm-rtms-debug">
      {/* ── Webhook participant events ───────────────────────── */}
      <div className="lm-rtms-debug__section-header">
        Participant Events ({systemEventLog.length})
      </div>
      {systemEventLog.length === 0 ? (
        <p className="lm-rtms-debug__empty">No participant events yet.</p>
      ) : (
        systemEventLog.map((entry, i) => (
          <div key={i} className="lm-rtms-debug__entry">
            <div className="lm-rtms-debug__type">
              {entry.eventType} &middot; {entry.participantRole}
            </div>
            <div className="lm-rtms-debug__participant-row">
              <span className="lm-rtms-debug__participant-name">{entry.participantName}</span>
              <span className="lm-rtms-debug__participant-id">{entry.participantId}</span>
            </div>
            <div className="lm-rtms-debug__participant-text">{entry.text}</div>
          </div>
        ))
      )}
      {/* ── Raw WS events (latest per non-chunk type) ────────── */}
      <div className="lm-rtms-debug__section-header">
        WS Events - latest per type ({wsEntries.length})
      </div>
      {wsEntries.length === 0 ? (
        <p className="lm-rtms-debug__empty">No WS events received yet.</p>
      ) : (
        wsEntries.map(([type, payload]) => (
          <div key={type} className="lm-rtms-debug__entry">
            <div className="lm-rtms-debug__type">{type}</div>
            <pre className="lm-rtms-debug__pre">{JSON.stringify(payload, null, 2)}</pre>
          </div>
        ))
      )}
      {/* ── RTMS transcript chunks - full rolling log ────────── */}
      <div className="lm-rtms-debug__section-header">
        RTMS Chunks - all ({rawTranscriptLog.length})
      </div>
      {rawTranscriptLog.length === 0 ? (
        <p className="lm-rtms-debug__empty">No transcript chunks received yet.</p>
      ) : (
        rawTranscriptLog.map((payload, i) => (
          <div key={i} className="lm-rtms-debug__entry">
            <pre className="lm-rtms-debug__pre">{JSON.stringify(payload, null, 2)}</pre>
          </div>
        ))
      )}
    </div>
  );
}
